import React from "react";
import PropTypes from "prop-types";
import {useTheme} from "connectui/theme/ThemeProvider";
import Container from "../layout/Container";
import {generateStyles} from "connectui/theme/styleUtilities";

/**
 * LoadingBar is a component that displays a horizontal progress bar.
 * It can be customized with the `progress`, `height`, and `color` props.
 *
 * @param {Object} props - The props for the LoadingBar component.
 * @param {number} [props.progress=0] - The current progress value (0-100).
 * @param {string} [props.height="4px"] - The height of the progress bar.
 * @param {string} [props.color="primary"] - The color base string of the progress bar. Defaults to "primary".
 * @param {Object} [props.sx={}] - Additional inline styles to apply to the container of the progress bar.
 *
 * @returns {JSX.Element} The rendered LoadingBar component.
 */
const LoadingBar = ({
  progress = 0,
  height = "4px",
  color = "primary",
  sx = {},
  ...props
}) => {
  const {theme} = useTheme();
  const barColor = theme.colors[color] || theme.colors.primary;

  const defaultContainerStyles = {
    width: "100%",
    backgroundColor: theme.colors.gray100,
    overflow: "hidden",
    height: height,
    borderRadius: theme.spacing(4),
  };
  const defaultBarStyles = {
    width: `${progress}%`,
    backgroundColor: barColor,
    height: "100%",
    transition: "width 0.3s ease",
    borderRadius: theme.spacing(4),
  };

  const containerStyles = generateStyles(
    {sx: {...defaultContainerStyles, ...sx}, ...props},
    theme,
  );

  const barStyles = generateStyles(
    {sx: {...defaultBarStyles, ...sx}, ...props},
    theme,
  );

  return (
    <Container sx={containerStyles} {...props}>
      <Container sx={barStyles} />
    </Container>
  );
};

LoadingBar.propTypes = {
  progress: PropTypes.number,
  height: PropTypes.string,
  color: PropTypes.string,
  sx: PropTypes.object,
};

export default LoadingBar;
